import React, { ReactNode, memo, useEffect } from 'react';
import { combineClassNames } from '../../utils/combineClassNames';
import Icon from '../icon';

export type DrawerProps = {
  show?: boolean;
  onHide?: () => void;
  children?: ReactNode;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  title?: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  hideCloseButton?: boolean;
  position?: 'left' | 'right';
  inline?: boolean; // New prop for inline drawer
};

const Drawer = memo(({
  show = false,
  onHide,
  children,
  className,
  size = 'medium',
  title,
  header,
  footer,
  hideCloseButton = false,
  position = 'right',
  inline = false,
}: DrawerProps) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && show && onHide) {
        onHide();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [show, onHide]);

  if (!show) return null;

  const sizeClasses = {
    small: 'tw-w-96',
    medium: 'tw-w-[600px]',
    large: 'tw-w-[800px]',
  };

  const positionClasses = {
    left: 'tw-left-0',
    right: 'tw-right-0',
  };

  // Inline drawer - positioned within container
  if (inline) {
    const drawerClassNames = combineClassNames(
      'tw-absolute tw-top-0 tw-h-full tw-bg-white tw-shadow-lg tw-flex tw-flex-col tw-border-l tw-border-gray-200',
      sizeClasses[size],
      positionClasses[position],
      className
    );

    return (
      <div className={drawerClassNames}>
        {/* Sticky Header */}
        {(title || header || !hideCloseButton) && (
          <div className="tw-sticky tw-top-0 tw-z-20 tw-bg-white tw-border-b tw-border-gray-200 tw-px-6 tw-py-4 tw-flex-shrink-0">
            {header || (
              <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                <div className="tw-flex-1">
                  {title && (
                    <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900">
                      {title}
                    </h2>
                  )}
                </div>
                {!hideCloseButton && (
                  <button
                    onClick={onHide}
                    className="tw-ml-4 tw-p-2 tw-rounded-md tw-text-gray-400
                     hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-transition-colors"
                  >
                    <Icon type="close" size={20} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="tw-flex-1 tw-overflow-y-auto">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="tw-flex-shrink-0 tw-border-t tw-border-gray-200 tw-px-6 tw-py-4">
            {footer}
          </div>
        )}
      </div>
    );
  }

  // Full-screen overlay drawer (original behavior)
  const drawerClassNames = combineClassNames(
    'tw-fixed tw-top-0 tw-h-full tw-bg-white tw-shadow-2xl tw-z-50 tw-flex tw-flex-col',
    sizeClasses[size],
    positionClasses[position],
    className
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-z-40"
        onClick={onHide}
      />

      {/* Drawer */}
      <div className={drawerClassNames}>
        {/* Sticky Header */}
        {(title || header || !hideCloseButton) && (
          <div className="tw-sticky tw-top-0 tw-z-20 tw-bg-white tw-border-b tw-border-gray-200 tw-px-6 tw-py-4 tw-flex-shrink-0">
            {header || (
              <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                <div className="tw-flex-1">
                  {title && (
                    <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900">
                      {title}
                    </h2>
                  )}
                </div>
                {!hideCloseButton && (
                  <button
                    onClick={onHide}
                    className="tw-ml-4 tw-p-2 tw-rounded-md tw-text-gray-400
                    hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-transition-colors"
                  >
                    <Icon type="close" size={20} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="tw-flex-1 tw-overflow-y-auto">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="tw-flex-shrink-0 tw-border-t tw-border-gray-200 tw-px-6 tw-py-4">
            {footer}
          </div>
        )}
      </div>
    </>
  );
});

Drawer.displayName = 'Drawer';

export default Drawer;
